import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { FixedButton } from '@vtex/butik/lib'
import QuantitySelector from '../Elements/QuantitySelector'
import CartMaxSkuQuantityLabel from '../Elements/CartMaxSkuQuantityLabel'

import BuyButton from './BuyButtonContainer'
import isEmpty from 'lodash/isEmpty'
import {
  isItemAvailableToAddToCart,
  isItemAvailableToEditQuantity,
  isItemWithoutStock,
} from '@/checkout-instore-legacy/components/SearchPage/utils/index'
import ItemShippingInfoAid from '@/checkout-instore-legacy/cart/components/ItemShippingInfoAid'
import CartLocation from '@/checkout-instore-legacy/cart/containers/CartLocationContainer'
import {
  getSimulationLogisticsInfo,
  isPickUpAvailable,
  isDeliveryAvailable,
} from '@/checkout-instore-legacy/cart/simulation/utils'
import { BuyNowButton } from '../BuyNowButton'
import { Button, csx } from '@vtex/admin-ui'

function BuyComponent({
  sku,
  skuIsAvailableAtInitialQuantity,
  manualPrice,
  onClickMoreDetails,
  showFixedButton,
  beforeAddToCart,
  stock,
  isLoadingStock,
  availableQuantity,
  displayShippingInfoAid,
  logisticsInfoCache,
  isSimulationLoading,
  hasSimulationError,
  loadSkuSimulation,
  sellWithoutStockInHands,
  clearSimulationError,
  shouldSimulationRefresh,
  clearLogisticsInfoCache,
  simulationRefreshed,
}) {
  const [quantity, setQuantity] = useState(1)

  const handleChangeQuantity = (e) => {
    const quantity = e.value
    setQuantity(quantity)
  }

  const handleRefresh = () => {
    loadSkuSimulation(sku, quantity)
    clearSimulationError()
  }

  const skuId = sku && sku.id

  const logisticsInfo = getSimulationLogisticsInfo(
    logisticsInfoCache,
    skuId,
    quantity
  )

  const stockIsEmpty = isEmpty(stock)
  const otherStoresQuantity = stockIsEmpty ? 0 : stock.otherStock
  const inStoreQuantity = stockIsEmpty ? 0 : stock.checkedInStock

  const hasCheckedInStock = inStoreQuantity > 0
  const hasDeliveryStock = isDeliveryAvailable(logisticsInfo, quantity)
  const hasPickUpStock = isPickUpAvailable(logisticsInfo, quantity)

  const quantitySelectorAvailable = isItemAvailableToEditQuantity({
    sellWithoutStockInHands,
    skuIsAvailableAtInitialQuantity,
  })
  const buyButtonAvailable = isItemAvailableToAddToCart({
    skuIsAvailableAtInitialQuantity,
    hasCheckedInStock,
    hasDeliveryStock,
    hasPickUpStock,
    sellWithoutStockInHands,
  })
  const withoutStock = isItemWithoutStock({
    quantity,
    availableQuantity,
    inStoreQuantity,
    otherStoresQuantity,
  })

  const isMissingLogisticsInfoCache = !sku // || (sku && logisticsInfoCache[`${sku.id}-${quantity}`] == null)
  const isLoading =
    isLoadingStock || isSimulationLoading || isMissingLogisticsInfoCache

  useEffect(() => {
    sku && loadSkuSimulation(sku, quantity)
  }, [loadSkuSimulation, quantity, sku])

  useEffect(() => {
    if (shouldSimulationRefresh) {
      clearLogisticsInfoCache()
      loadSkuSimulation(sku, quantity)
      simulationRefreshed()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSimulationRefresh])

  const buyButtonProps = {
    skuId,
    available: buyButtonAvailable,
    quantity,
    manualPrice,
    withoutStock,
    beforeAddToCart,
    isLoading,
    attachments: sku?.attachments,
  }

  const hasMoreDetailsButton = !!onClickMoreDetails

  return (
    <React.Fragment>
      {showFixedButton && (
        <FixedButton>
          <div className="dn-l bg-base w-100 pa3">
            <BuyButton {...buyButtonProps} />
          </div>
        </FixedButton>
      )}

      <div className="w-100">
        {sku && (
          <React.Fragment>
            <div
              className={`flex flex-row ${
                sku.refId ? 'justify-between' : 'justify-end'
              }`}
            >
              {sku.refId ? (
                <span
                  className="t-small c-muted-2"
                  data-testid="product-availaility"
                >
                  <FormattedMessage id="productAvailability" />
                </span>
              ) : null}
              <CartMaxSkuQuantityLabel />
            </div>
            <div className="w-100 mv3">
              <QuantitySelector
                onChangeAmount={handleChangeQuantity}
                amount={quantity}
                inStoreQuantity={inStoreQuantity}
                isLoadingStock={isLoadingStock}
                hasDeliveryStock={hasDeliveryStock}
                hasPickUpStock={hasPickUpStock}
                isSimulationLoading={isLoading}
                hasSimulationError={hasSimulationError}
                handleRefresh={handleRefresh}
                isItemAvailable={quantitySelectorAvailable}
                skuId={skuId}
              />
            </div>
          </React.Fragment>
        )}

        <div>
          <div
            className={`pt7 ${!hasMoreDetailsButton ? 'w-100' : ''} ${
              showFixedButton ? 'dn dib-l' : 'dib'
            }`}
          >
            <BuyNowButton {...buyButtonProps} />
          </div>
          {hasMoreDetailsButton && (
            <div className="dib" id="more-details">
              <Button
                variant="tertiary"
                onClick={onClickMoreDetails}
                className={csx({ marginLeft: '$space-2' })}
                disabled={isSimulationLoading}
              >
                <FormattedMessage id="search.moreDetails" />
              </Button>
            </div>
          )}
        </div>

        {displayShippingInfoAid && (
          <React.Fragment>
            <div className="pt6 pt7-ns">
              <CartLocation context="Product Page" withBorder />
            </div>
            <div className="pt6 pt7-ns">
              <ItemShippingInfoAid
                logisticsInfo={logisticsInfo}
                quantity={quantity}
                isLoading={isLoading}
                stock={stock}
              />
            </div>
          </React.Fragment>
        )}
      </div>
    </React.Fragment>
  )
}

BuyComponent.defaultProps = {
  showGoToProductPage: false,
  showFixedButton: false,
}

BuyComponent.propTypes = {
  sku: PropTypes.object,
  skuIsAvailableAtInitialQuantity: PropTypes.bool.isRequired,
  manualPrice: PropTypes.number,
  onClickMoreDetails: PropTypes.func,
  showFixedButton: PropTypes.bool,
  beforeAddToCart: PropTypes.func,
  stock: PropTypes.object,
  isLoadingStock: PropTypes.bool,
  availableQuantity: PropTypes.number,
  sellWithoutStockInHands: PropTypes.bool.isRequired,
  displayShippingInfoAid: PropTypes.bool,
  logisticsInfoCache: PropTypes.object,
  isSimulationLoading: PropTypes.bool,
  hasSimulationError: PropTypes.bool,
  loadSkuSimulation: PropTypes.func.isRequired,
  clearSimulationError: PropTypes.func.isRequired,
  clearLogisticsInfoCache: PropTypes.func.isRequired,
  shouldSimulationRefresh: PropTypes.bool,
  simulationRefreshed: PropTypes.func.isRequired,
}

export default BuyComponent
